import { useEffect, useRef, useState } from 'react';

export const DEFAULT_WAIT = 400;

/**
 * @param value
 * The value to debounce
 * @param wait
 * The wait time in milliseconds
 *  * Common wait ranges:
 * 300-500ms: Most common range used in production
 * 200ms: Used when immediate feedback is crucial
 * 1000ms: Used for more intensive operations
 * @param options.leading
 * Whether to call the function on the leading edge of the debounce
 * @returns
 * The debounced value
 */
export function useDebouncedValue<T = unknown>(
  value: T,
  wait = DEFAULT_WAIT,
  options = { leading: false },
) {
  const [_value, setValue] = useState(value);
  const mountedRef = useRef(false);
  const timeoutRef = useRef<number | null>(null);
  const cooldownRef = useRef(false);

  const cancel = () => window.clearTimeout(timeoutRef.current!);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No dependencies needed
  useEffect(() => {
    if (mountedRef.current) {
      if (!cooldownRef.current && options.leading) {
        cooldownRef.current = true;
        setValue(value);
      } else {
        cancel();
        timeoutRef.current = window.setTimeout(() => {
          cooldownRef.current = false;
          setValue(value);
        }, wait);
      }
    }
  }, [value, options.leading, wait]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No dependencies needed
  useEffect(() => {
    mountedRef.current = true;
    return cancel;
  }, []);

  return _value;
}
